import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import PartnerAvatarInfo from 'modules/_shared/components/PartnerAvatarInfo';
import InputNumber from 'modules/_shared/components/InputNumber';
import InputRadio from 'modules/_shared/components/InputRadio';
import InputDate from 'modules/_shared/components/InputDate';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import InputTextArea from 'modules/_shared/components/InputTextArea';

import { TradingOfferType } from 'types/TradingOffer';
import { BLANK_PROFILE_PICTURE } from 'constants/defaultConstants';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { useAddTradingOfferPage } from '../context/AddTradingOfferPageContext';
import { addTradingOfferLabels } from '../constants/addTradingOfferLabels';

const VALIDITY_PERIOD_DAYS = [7, 15, 30, 60, 90];

const AddTradingOfferDataSection: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAddTradingOfferPage();

  const labels = useMemo(
    () => addTradingOfferLabels[data.tradingOfferType as TradingOfferType],
    [data.tradingOfferType]
  );

  const isSellOffer = useMemo(
    () => data.tradingOfferType === TradingOfferType.SELL,
    [data.tradingOfferType]
  );

  const sharesAfterSale = data.partnerCurrentSharesCount
    ? data.partnerCurrentSharesCount - data.sharesToTransfer
    : 0;

  const selectedValidityDays = useMemo(() => {
    if (!data.startDate || !data.endDate) return null;
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    const diffDays = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    return VALIDITY_PERIOD_DAYS.includes(diffDays) ? diffDays : null;
  }, [data.startDate, data.endDate]);

  return (
    <div className="col-lg-7">
      {/* Card 1: Partner */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-user text-primary mr-2" />
            {t(labels.SELECT_PARTNER)}
          </h6>

          {isSellOffer && (
            <div className="row  mb-3">
              <div className="col-12 col-lg-5">
                <PartnerAvatarInfo
                  id={data.selectedPartnerOption?.value || ''}
                  name={
                    data.selectedPartnerOption?.name ||
                    data.selectedPartnerOption?.label ||
                    '-'
                  }
                  email={data.selectedPartnerOption?.email || '-'}
                  image={
                    data.selectedPartnerOption?.image || BLANK_PROFILE_PICTURE
                  }
                  societyId={data.societyId}
                  haveStatus={false}
                  haveLink={false}
                />
                {data.isPartnerView && !data.selectedPartnerOption && (
                  <div className="text-soft">{t('LoadingPartners')}</div>
                )}
              </div>

              <div className="col-6 col-lg-3 d-flex flex-column">
                <span className="text-soft">{t('CurrentShares')}</span>
                <span className="text-left">
                  {formatNumber(data.partnerCurrentSharesCount)}
                </span>
              </div>

              <div className="col-6 col-lg-4 d-flex flex-column">
                <span className="text-soft">{t('SharesAfterSale')}</span>
                <span className="text-left text-primary">
                  {formatNumber(sharesAfterSale)}
                </span>
              </div>
            </div>
          )}

          {data.isPartnerView && !isSellOffer && (
            <div className="form-group mb-0">
              <div className="form-control-wrap">
                {data.selectedPartnerOption ? (
                  <PartnerAvatarInfo
                    id={data.selectedPartnerOption.value}
                    name={
                      data.selectedPartnerOption.name ||
                      data.selectedPartnerOption.label
                    }
                    email={data.selectedPartnerOption.email || ''}
                    image={data.selectedPartnerOption.image}
                    societyId={data.societyId}
                    haveStatus={false}
                    haveLink={false}
                  />
                ) : (
                  <div className="text-soft">{t('LoadingPartners')}</div>
                )}
              </div>
            </div>
          )}

          {!data.isPartnerView && (
            <div className="form-group mb-0">
              <label className="form-label" htmlFor="partner-select-input">
                {t(labels.SELECT_PARTNER)} <MandatoryField />
              </label>
              <div className="form-control-wrap">
                <CustomAsyncSelect
                  id="partner-select-input"
                  value={data.selectedPartnerOption}
                  onChange={actions.handleChangePartner}
                  onFetchData={actions.handleSearchPartnerByTerm}
                  placeholder={t('SearchPartner')}
                  minimumInputLength={3}
                  noDataLabelKey="NoPartnersHaveBeenFound"
                  loadingLabelKey="LoadingPartners"
                  errorLabelKey="ErrorLoadingPartners"
                  defaultOptions={data.partnerOptions}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Shares */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-layers text-primary mr-2" />
            {t('Shares')}
          </h6>

          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="form-group">
                <label className="form-label" htmlFor="class-select-input">
                  {`${t(labels.SELECT_CLASS)} (${t('Optional')})`}
                </label>
                <div className="form-control-wrap">
                  <CustomAsyncSelect
                    id="class-select-input"
                    value={data.selectedHoldingClassOption}
                    onChange={actions.handleChangeHoldingClass}
                    onFetchData={actions.handleSearchHoldingClassByTerm}
                    placeholder={t('SearchClass')}
                    minimumInputLength={1}
                    noDataLabelKey="NoClassesHaveBeenFound"
                    loadingLabelKey="LoadingClasses"
                    errorLabelKey="ErrorLoadingClasses"
                    defaultOptions={data.holdingClassOptions}
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <InputNumber
                id="shares-to-transfer-input"
                label={t(labels.SHARES_TO_TRANSFER)}
                value={data.sharesToTransfer}
                decimalScale={2}
                max={data.maxSharesToTransfer}
                onChange={actions.handleChangeSharesToTransfer}
                required
              />
            </div>

            {isSellOffer && (
              <div className="col-12">
                <InputCheckbox
                  id="sell-all-my-shares-checkbox"
                  label={t('SellAllMyShares')}
                  checked={data.isSellAllMyShares}
                  onChange={actions.handleChangeIsSellAllMyShares}
                  disabled={
                    !data.selectedPartnerOption ||
                    !data.selectedHoldingClassOption ||
                    data.maxSharesToTransfer <= 0
                  }
                  marginBottom={0}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 3: Price */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-coin text-primary mr-2" />
            {t('Price')}
          </h6>

          <div className="row ">
            <div className="col-md-6 mb-4">
              <InputNumber
                id="price"
                label={
                  data.isPricePerShare ? t(labels.PRICE) : t(labels.PRICE_TOTAL)
                }
                value={data.price}
                iconRight="icon ni ni-sign-euro"
                decimalScale={2}
                onChange={actions.handleChangePrice}
                required
              />
            </div>

            <div className="col-md-6 mb-4">
              <InputNumber
                id="total-offer-price"
                label={t('EstimatedTotalAmount')}
                value={data.totalOfferPrice}
                iconRight="icon ni ni-sign-euro"
                decimalScale={2}
                onChange={() => {}}
                disabled
              />
            </div>

            {isSellOffer && (
              <>
                <div className="col-md-6 d-flex align-items-center">
                  <InputRadio
                    id="price-per-share-radio"
                    checked={data.isPricePerShare}
                    label={t('PricePerShare')}
                    description={t(labels.PRICE_PER_SHARE_INFO)}
                    onChange={actions.handleChangeIsPricePerShare}
                  />
                </div>

                <div className="col-md-6 d-flex align-items-center">
                  <InputRadio
                    id="total-offer-price-radio"
                    checked={!data.isPricePerShare}
                    label={t('TotalOfferPrice')}
                    description={t(labels.PRICE_TOTAL_INFO)}
                    onChange={actions.handleChangeIsPricePerShare}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Card 4: Validity */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-calendar text-primary mr-2" />
            {t('TradingOfferValidityPeriod')}
          </h6>

          <div className="row ">
            <div className="col-md-6 mb-4">
              <InputDate
                id="startDate"
                label={t('StartDate')}
                value={data.startDate}
                onChange={actions.handleChangeStartDate}
                required
                lgSize
              />
            </div>

            <div className="col-md-6 mb-4">
              <InputDate
                id="endDate"
                label={t('EndDate')}
                value={data.endDate}
                onChange={actions.handleChangeEndDate}
                minDate={data.startDate || undefined}
                required
                lgSize
              />
            </div>

            <div className="col-12">
              <div className="d-flex w-100" style={{ gap: '8px' }}>
                {VALIDITY_PERIOD_DAYS.map((days) => (
                  <Button
                    key={days}
                    className="flex-fill"
                    onClick={() => actions.handleSetDatePreset(days)}
                    variant={
                      selectedValidityDays === days
                        ? ButtonVariants.PRIMARY
                        : ButtonVariants.LIGHT
                    }
                  >
                    <span className="text-center">
                      {t('NDays', { count: days })}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card 5: Advanced Options */}
      <div className="card card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">
            <em className="icon ni ni-setting text-primary mr-2" />
            {t('AdvancedOptions')}
          </h6>
          <div className="row">
            {isSellOffer && (
              <div className="col-md-6 mb-4">
                <InputCheckbox
                  id="permitir-parcial-checkbox"
                  label={t('IAllowPartialAcceptance')}
                  checked={data.isPartialAmountEnabled}
                  onChange={actions.handleChangeIsPartialAmountEnabled}
                  tooltip={t('IAllowPartialAcceptanceTooltip')}
                  marginBottom={0}
                />
              </div>
            )}

            {isSellOffer && data.isPartialAmountEnabled && (
              <div className="col-md-6" id="minimoContainer">
                <InputNumber
                  id="minimo-acciones-input"
                  label={t('MinSharesPerAcceptance')}
                  value={data.minSharesPerAcceptance}
                  decimalScale={0}
                  onChange={actions.handleChangeMinSharesPerAcceptance}
                  infoLabel={t('MinSharesPerAcceptanceNote')}
                />
              </div>
            )}
            <div className="col-12">
              <InputTextArea
                id="notas-input"
                label={`${t('AdditionalNotes')} (${t('Optional')})`}
                description={t('VisibleForAllPartners')}
                value={data.additionalNotes}
                onChange={actions.handleChangeAdditionalNotes}
                placeholder={t('AdditionalNotesPlaceholder')}
                rows={3}
                showCounter={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTradingOfferDataSection;
